import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { RestService } from '../../../services/rest/rest.service';
import { CommonService } from '../../../services/common/common.service';
import { ExternalAreaModalType, HelpIndex, Endpoint, TagOperationType } from '../../../models/enums';
import { DataAreaAndTreeComponent } from '../../data-area-and-tree/data-area-and-tree.component';
import { TagsModalComponent, TagsModalData } from '../tags-modal/tags-modal.component';
import { TagApplication } from '../../../models/tag';
import { GenericColDef, SortByFilter, GridContext, GridRowSelectionType } from '../../../models/grid-context';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { SelectionChangedEvent } from 'ag-grid-community';
import { DrEntity } from '../../../models/dr-entity';

@Component({
    standalone: false,
    selector: 'res-copy-to-load-from-external-area-modal',
    templateUrl: './copy-to-load-from-external-area-modal.component.html',
    styleUrls: ['./copy-to-load-from-external-area-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CopyToLoadFromExternalAreaModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(DataAreaAndTreeComponent) dataAreaAndTreeComponent: DataAreaAndTreeComponent;
    @ViewChild('filesGrid') filesGrid: GenericGridComponent;

    modalTitle: string;
    modalAutomationId: string;
    modalContainerAutomationId: string;
    modalHelpIndex: HelpIndex;
    primaryButtonLabel: string;
    modalClass = 'copy-to-load-from-modal';
    resizeHorizontalMin = 500;
    modalType: ExternalAreaModalType;
    selectedPath: string = null;
    loadOperationType = 'docs-only';
    selectedTagsList = new Array<TagApplication>();
    applyNewTags: boolean;
    applyParentTags = true;
    docsOnlyUpdateNative: boolean;
    docsWithChildrenUpdateNative: boolean;
    syncToProjectData = true;
    selectedFile = { name: '', handle: '' };
    fileGridRequestInfo: DrRequestInfo;
    filesGridRowSelection = GridRowSelectionType.single;

    filesGridColumnDefs = new Array<GenericColDef>({
        headerName: 'File Name',
        field: 'name',
        automationId: '',
        lockVisible: true,
        sortBy: SortByFilter.NAME.toString(),
    });

    filesGridContext = new GridContext({
        sortAscending: true,
        sortEnabled: false,
        sortBy: SortByFilter.NAME.toString(),
    });

    filters = [{
        attribute: 'fileExtension',
        operator: 'EQUALS',
        value: 'lfp'
    },
    {
        attribute: 'fileExtension',
        operator: 'EQUALS',
        value: 'opt'
    }];

    constructor(private modalService: BsModalService, private modalRef: BsModalRef, private restService: RestService, private commonService: CommonService) {
        super(modalService, modalRef);
    }

    filesGridResponseArrayFilter = (value: any) => {
        if (value.leaf) {
            return new DrEntity(value);
        }
    }

    ngOnInit() {
        if (this.modalData) {
            this.modalType = this.modalData.modalType;
            switch (this.modalType) {
                case ExternalAreaModalType.copyFor:
                    this.modalTitle = 'Copy For External Imaging';
                    this.modalHelpIndex = HelpIndex.external_imaging_copy_for_dlg;
                    this.modalContainerAutomationId = 'copy-for-external-imaging';
                    this.primaryButtonLabel = 'Copy';
                    break;
                case ExternalAreaModalType.copyTo:
                    this.modalTitle = 'Copy To External Area';
                    this.modalHelpIndex = HelpIndex.copy_to_ext_area_reprocess_dlg;
                    this.modalContainerAutomationId = 'copy-to-external-area';
                    this.primaryButtonLabel = 'Copy';
                    break;
                case ExternalAreaModalType.loadFrom:
                    this.modalTitle = 'Load From External Area';
                    this.modalHelpIndex = HelpIndex.load_from_ext_area_reprocess_dlg;
                    this.modalContainerAutomationId = 'load-from-external-area';
                    this.primaryButtonLabel = 'Load';
                    this.resizeHorizontalMin = 640;
                    break;
                case ExternalAreaModalType.importExternal:
                    this.modalTitle = 'Import External Images';
                    this.modalHelpIndex = HelpIndex.external_image_import_dlg;
                    this.modalContainerAutomationId = 'import-external-images';
                    this.primaryButtonLabel = 'Import';
                    this.modalClass += ' import-external-images';
                    break;
            }
            this.modalAutomationId = this.modalContainerAutomationId + '-modal';
            this.filesGridColumnDefs[0].automationId = this.modalAutomationId;
        }
    }

    filesGridOnSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.selectedFile = event.api.getSelectedRows()[0];
            if (!this.selectedFile) {
                this.selectedFile = { name: '', handle: null };
            }
        }
    }

    isPrimaryButtonEnabled(): boolean {
        if (this.modalType === ExternalAreaModalType.importExternal) {
            return this.selectedFile.name !== '';
        } else {
            return this.selectedPath != null;
        }
    }

    onDataAreaAndTreeChange(): void {
        this.selectedPath = this.dataAreaAndTreeComponent.selectedPath ? this.dataAreaAndTreeComponent.selectedPath.handle : null;

        if (this.selectedPath && this.selectedPath.includes(this.dataAreaAndTreeComponent.selectedDataArea.path)) {
            this.selectedPath = this.selectedPath.replace(this.dataAreaAndTreeComponent.selectedDataArea.path, '');

            if (this.modalType === ExternalAreaModalType.importExternal) {
                this.selectedFile = { name: '', handle: '' };
                this.fileGridRequestInfo = {
                    endpoint: Endpoint.connectorManager_exploreConnector,
                    responseName: 'paths',
                    data: {
                        connectorHandle: this.dataAreaAndTreeComponent.selectedDataArea.connectorHandle,
                        filters: this.filters, parentPath: this.dataAreaAndTreeComponent.selectedPath
                    }
                };
                this.filesGrid.requestInfo = this.fileGridRequestInfo;
                this.filesGrid.loadData();
            }
        }
    }

    isDataAreaSelected(): boolean {
        return this.dataAreaAndTreeComponent.selectedDataArea;
    }

    setLoadOperationType(type: string): void {
        this.loadOperationType = type;
    }

    getSelectedTagsNo(): string {
        return this.selectedTagsList.length + ' tags';
    }

    openTagSelector(): void {
        this.commonService.openModal(TagsModalComponent, {
            modalData: <TagsModalData>{ operationType: TagOperationType.select, selectedTags: this.selectedTagsList }
        }).then((modalResponse) => {
            if (modalResponse && modalResponse.modalResult && modalResponse.modalData) {
                this.selectedTagsList = modalResponse.modalData;
            }
        });
    }

    ok(): void {
        if (this.selectedPath) {
            if (this.modalType === ExternalAreaModalType.copyTo || this.modalType === ExternalAreaModalType.copyFor) {
                const requestData = {
                    dataAreaHandle: this.dataAreaAndTreeComponent.selectedDataArea.handle,
                    externalDocumentsPath: this.selectedPath,
                    includeFileName: this.modalType === ExternalAreaModalType.copyTo,
                    viewHandle: this.modalData.viewHandle
                };
                this.restService.post(Endpoint.corpusManager_copyDocsToLocation, requestData);
            } else if (this.modalType === ExternalAreaModalType.importExternal) {
                const requestData = {
                    dataAreaHandle: this.dataAreaAndTreeComponent.selectedDataArea.handle,
                    loadFileRelativePath: this.selectedPath + '/' + this.selectedFile.name,
                    viewHandle: this.modalData.viewHandle
                };
                this.restService.post(Endpoint.workingSetManager_importBaselineImageLoadFile, requestData);
            } else {
                const requestData = {
                    dataAreaHandle: this.dataAreaAndTreeComponent.selectedDataArea.handle,
                    externalDocumentRelativePath: this.selectedPath,
                    processExternalOCRText: this.loadOperationType === 'external-ocr',
                    reprocessDocumentsOnly: this.loadOperationType === 'docs-only',
                    reprocessDocumentsWithChildren: this.loadOperationType === 'docs-with-children',
                    taskDescription: 'Externally reprocessing documents from view ' + this.modalData.viewName,
                    updateNativeDocument: false,
                    viewHandle: this.modalData.viewHandle
                };
                if (this.loadOperationType === 'docs-only') {
                    requestData.updateNativeDocument = this.docsOnlyUpdateNative;
                } else if (this.loadOperationType === 'docs-with-children') {
                    requestData.updateNativeDocument = this.docsWithChildrenUpdateNative;
                    const specificData = {
                        newChildrenTags: this.syncToProjectData && this.applyNewTags && this.selectedTagsList.length > 0 ? this.selectedTagsList : null,
                        syncDocumentChildrenTagState: this.syncToProjectData && this.applyParentTags,
                        syncDocumentChildrenToCaseData: this.syncToProjectData
                    };
                    Object.assign(requestData, specificData);
                }
                this.restService.post(Endpoint.corpusManager_reprocessFromView, requestData);
            }
            this.closeModal();
        }
    }
}
